/**
 * Compat layer over the shadcn primitives: feature code imports Badge, Card,
 * Spinner, PageSpinner, Dialog (simple open/onClose API), EmptyState from here.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog as DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog';

export { Card } from './card';
export { Skeleton } from './skeleton';
export { Separator } from './separator';

// ── Badge (chips estilo Tiflux: fundo + borda + texto tintados, radius 4px) ─
// Os hexes de status são a exceção sancionada pela spec (trios de chip antd).
export const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-sm border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'border-border bg-muted text-muted-foreground',
        primary: 'border-primary/40 bg-primary/10 text-primary-pressed',
        success: 'border-[#B7EB8F] bg-[#F6FFED] text-[#52C41A]',
        warning: 'border-[#FFE58F] bg-[#FFFBE6] text-[#D48806]',
        destructive: 'border-[#FFA39E] bg-[#FFF1F0] text-[#FF4D4F]',
        info: 'border-[#91CAFF] bg-[#E6F4FF] text-[#1677FF]',
        outline: 'border-border bg-card text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// ── Spinner ────────────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'size-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent',
        className
      )}
    />
  );
}

export function PageSpinner() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner className="size-8" />
    </div>
  );
}

// ── Dialog (simple API over shadcn/radix dialog) ───────────────────────────
export function Dialog({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <DialogRoot open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={cn('max-h-[85vh] overflow-y-auto', className)}>
        {title ? (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        ) : null}
        {children}
      </DialogContent>
    </DialogRoot>
  );
}

// ── Empty state (glifo inbox + mensagem, padrão "Não há dados") ────────────
export function EmptyState({ message, className }: { message: string; className?: string }) {
  return (
    <div
      className={cn(
        'flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground',
        className
      )}
    >
      <Inbox className="size-8 text-muted-foreground/40" strokeWidth={1.5} aria-hidden />
      <span className="text-sm">{message}</span>
    </div>
  );
}
